import {
  convexAction,
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Checkbox } from '@rekrytering/ui/components/checkbox'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@rekrytering/ui/components/drawer'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type PdfExportDrawerProps = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly jobId: Id<'jobs'>
  readonly exportType: 'transcripts' | 'reports'
}

type DrawerPhase =
  | { readonly status: 'selecting' }
  | { readonly status: 'generating'; readonly exportJobId: Id<'pdfExportJobs'> }
  | { readonly status: 'error' }

export const PdfExportDrawer = ({
  open,
  onOpenChange,
  jobId,
  exportType,
}: PdfExportDrawerProps) => {
  const { t } = useTranslation()
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<Id<'interviews'>>>(
    new Set(),
  )
  const [phase, setPhase] = useState<DrawerPhase>({ status: 'selecting' })

  const { data: candidates, isLoading } = useQuery({
    ...convexAction(
      api.features.interviews.pdf_export_public
        .listMyCompletedInterviewsForExport,
      { jobId },
    ),
    enabled: open,
  })

  useEffect(() => {
    if (candidates && candidates.length > 0) {
      setSelectedIds(new Set(candidates.map((c) => c.interviewId)))
    }
  }, [candidates])

  useEffect(() => {
    if (!open) {
      setPhase({ status: 'selecting' })
      setSelectedIds(new Set())
    }
  }, [open])

  const { mutateAsync: createExportJob } = useMutation({
    mutationFn: useConvexMutation(
      api.features.interviews.pdf_export_public.createMyPdfExportJob,
    ),
  })

  const { mutateAsync: generateBulkPdfs } = useMutation({
    mutationFn: useConvexAction(
      api.features.interviews.pdf_export.generateMyBulkInterviewPdfs,
    ),
  })

  const exportJobId = phase.status === 'generating' ? phase.exportJobId : null

  const { data: exportJob } = useQuery(
    convexQuery(
      api.features.interviews.pdf_export_public.getMyPdfExportJob,
      exportJobId ? { exportJobId } : 'skip',
    ),
  )

  useEffect(() => {
    if (exportJob?.status === 'completed' && exportJob.downloadUrl) {
      const anchor = document.createElement('a')
      anchor.href = exportJob.downloadUrl
      anchor.download = ''
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      onOpenChange(false)
    }
    if (exportJob?.status === 'failed') {
      setPhase({ status: 'error' })
    }
  }, [exportJob?.status, exportJob?.downloadUrl, onOpenChange])

  const handleStartDownload = async (
    idsToExport: ReadonlyArray<Id<'interviews'>>,
  ): Promise<void> => {
    const result = await createExportJob({
      jobId,
      type: exportType,
      candidateCount: idsToExport.length,
    })
    if (!result) {
      setPhase({ status: 'error' })
      return
    }

    setPhase({ status: 'generating', exportJobId: result.exportJobId })

    generateBulkPdfs({
      exportJobId: result.exportJobId,
      interviewIds: [...idsToExport],
      type: exportType,
    }).catch(() => {
      setPhase({ status: 'error' })
    })
  }

  const handleDownloadSelected = async (): Promise<void> => {
    await handleStartDownload([...selectedIds])
  }

  const handleDownloadAll = async (): Promise<void> => {
    if (!candidates) return
    await handleStartDownload(candidates.map((c) => c.interviewId))
  }

  const handleToggleAll = (): void => {
    if (!candidates) return
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(candidates.map((c) => c.interviewId)))
    }
  }

  const handleToggleCandidate = (interviewId: Id<'interviews'>): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(interviewId)) {
        next.delete(interviewId)
      } else {
        next.add(interviewId)
      }
      return next
    })
  }

  const title =
    exportType === 'transcripts'
      ? t('interviews.pdfExport.drawerTitleTranscripts')
      : t('interviews.pdfExport.drawerTitleReports')

  const allSelected =
    candidates != null && selectedIds.size === candidates.length

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>
            {t('interviews.pdfExport.drawerDescription')}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4">
          {phase.status === 'selecting' && (
            <>
              {isLoading && (
                <p className="text-sm text-muted-foreground py-4">Loading...</p>
              )}

              {!isLoading && candidates && candidates.length === 0 && (
                <p className="text-sm text-muted-foreground py-4">
                  {t('interviews.pdfExport.noCompletedCandidates')}
                </p>
              )}

              {!isLoading && candidates && candidates.length > 0 && (
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground"
                    onClick={handleToggleAll}
                  >
                    <Checkbox checked={allSelected} readOnly />
                    <span>
                      {allSelected
                        ? t('interviews.pdfExport.deselectAll')
                        : t('interviews.pdfExport.selectAll')}
                    </span>
                  </button>

                  <div className="border-t pt-2">
                    {candidates.map((candidate) => (
                      <button
                        key={candidate.interviewId}
                        type="button"
                        className="flex w-full items-center gap-3 rounded-md px-2 py-2 hover:bg-accent"
                        onClick={() =>
                          handleToggleCandidate(candidate.interviewId)
                        }
                      >
                        <Checkbox
                          checked={selectedIds.has(candidate.interviewId)}
                          readOnly
                        />
                        {candidate.thumbnailUrl ? (
                          <img
                            src={candidate.thumbnailUrl}
                            alt=""
                            className="h-9 w-9 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                            {candidate.candidateName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex flex-col items-start text-left">
                          <span className="text-sm font-medium text-foreground">
                            {candidate.candidateName}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {candidate.candidateEmail}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {phase.status === 'generating' && exportJob && (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-sm text-muted-foreground">
                {t('interviews.pdfExport.generating', {
                  completed: String(exportJob.completed),
                  total: String(exportJob.total),
                })}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPhase({ status: 'selecting' })}
              >
                {t('interviews.pdfExport.cancel')}
              </Button>
            </div>
          )}

          {phase.status === 'error' && (
            <div className="py-8 text-center">
              <p className="text-sm text-destructive">
                {t('interviews.pdfExport.error')}
              </p>
            </div>
          )}
        </div>

        {phase.status === 'selecting' &&
          candidates != null &&
          candidates.length > 0 && (
            <DrawerFooter>
              <Button
                onClick={handleDownloadSelected}
                disabled={selectedIds.size === 0}
              >
                {t('interviews.pdfExport.downloadSelected')}
              </Button>
              <Button variant="outline" onClick={handleDownloadAll}>
                {t('interviews.pdfExport.downloadAll')}
              </Button>
            </DrawerFooter>
          )}
      </DrawerContent>
    </Drawer>
  )
}
